import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { Lang, i18n } from "@/i18n-config";

type SymbolInfoParams = {
  symbol: string;
  lang: Lang;
  token?: string;
};

const isSlideExistSchema = z.object({
  data: z.object({
    isExist: z.boolean().nullish(),
  }),
  message: z.string(),
});

export const getIsSlideExist = async ({
  symbol,
  lang,
  token,
}: SymbolInfoParams): Promise<z.infer<typeof isSlideExistSchema>["data"]> => {
  const endpoint = `/events-slide-is-exist/${symbol}`;

  if (!symbol || !lang) {
    throw new Error(`GET: ${endpoint}\n Missing required params`);
  }
  if (!i18n.locales.includes(lang)) {
    throw new Error(`GET: ${endpoint}\n Invalid lang: ${lang}`);
  }
  if (!i18n.locales.includes(lang)) {
    throw new Error(`Invalid lang: ${lang}`);
  }

  const headers = new Headers({ "Accept-Language": lang });
  if (token) {
    headers.append("X-User-Profile", token);
  }

  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "GET",
    headers,
  });
  if (!res.ok)
    throw new Error(`Failed to fetch is slide exist ${symbol}:
    Status: ${res.status}
    Response Text: ${await res.text()}`);

  return isSlideExistSchema.parse(await res.json()).data;
};

export const useIsSlideExist = (symbol: string) => {
  const { lang } = useLang();
  const { backendToken } = useAuth();
  return useQuery(
    ["is-slide-exist", symbol, lang, backendToken],
    () => getIsSlideExist({ symbol, lang, token: backendToken }),
    { enabled: !!symbol }
  );
};
